import Aes from 'react-native-aes-crypto';

// Current OWASP 2024 recommendation for PBKDF2-SHA256.
export const PBKDF2_ITERATIONS = 210000;

// 32-byte AES-256 key + 32-byte HMAC-SHA-256 key, encoded as 128 hex chars.
const COMBINED_KEY_BYTES = 64;
const COMBINED_KEY_HEX_LEN = COMBINED_KEY_BYTES * 2;
const ENC_KEY_HEX_LEN = 32 * 2;
// react-native-aes-crypto's native pbkdf2 takes the output length in BITS,
// not bytes (see node_modules/react-native-aes-crypto/ios/Aes/lib/AesCrypt.m
// — `dataWithLength:length/8`). randomKey() in the same module uses bytes.
// Don't confuse the two — pass _BITS to pbkdf2, _BYTES to randomKey.
const COMBINED_KEY_BITS = COMBINED_KEY_BYTES * 8;

// AES block size.
const IV_BYTES = 16;
const IV_HEX_LEN = IV_BYTES * 2;

// Per-user salt length (128 bits is sufficient when random).
const SALT_BYTES = 16;

export const CURRENT_VERSION = 'v2' as const;

export interface EncryptedEnvelopeV2 {
  version: 'v2';
  iv: string;         // hex, 32 chars
  ciphertext: string; // base64
  tag: string;        // hex, 64 chars (HMAC-SHA-256)
}

export class DecryptionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DecryptionError';
  }
}

/**
 * Compare two hex strings in constant time. Returns false immediately only on
 * length mismatch (length is not secret).
 */
export function constantTimeEqualHex(a: string, b: string): boolean {
  if (typeof a !== 'string' || typeof b !== 'string') return false;
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diff === 0;
}

function splitKey(combinedHex: string): { encKey: string; macKey: string } {
  if (
    typeof combinedHex !== 'string' ||
    combinedHex.length !== COMBINED_KEY_HEX_LEN
  ) {
    throw new Error(
      `DocumentEncryptionV2: key must be ${COMBINED_KEY_HEX_LEN} hex chars (got ${
        combinedHex?.length ?? 'none'
      })`,
    );
  }
  return {
    encKey: combinedHex.slice(0, ENC_KEY_HEX_LEN),
    macKey: combinedHex.slice(ENC_KEY_HEX_LEN),
  };
}

/**
 * Generate a fresh 64-byte (128 hex char) key suitable for use as a
 * per-document key. First 32 bytes are used for AES, last 32 for HMAC.
 */
export async function generateKey(): Promise<string> {
  return Aes.randomKey(COMBINED_KEY_BYTES);
}

/**
 * Generate a fresh per-user salt.
 */
export async function generateSalt(): Promise<string> {
  return Aes.randomKey(SALT_BYTES);
}

/**
 * Derive a 64-byte key from a user-supplied password and a per-user salt using
 * native PBKDF2-SHA256. Returns a 128-char hex string (enc + mac halves).
 */
export async function deriveKey(
  password: string,
  salt: string,
  iterations: number = PBKDF2_ITERATIONS,
): Promise<string> {
  if (!password) throw new Error('deriveKey: password is required');
  if (!salt) throw new Error('deriveKey: salt is required');
  if (iterations < 100000) {
    throw new Error(
      `deriveKey: iterations must be >= 100000 (got ${iterations})`,
    );
  }
  return Aes.pbkdf2(password, salt, iterations, COMBINED_KEY_BITS, 'sha256');
}

/**
 * Encrypt a UTF-8 plaintext under the given 64-byte combined key. The output
 * is AES-256-CBC with a fresh random IV, authenticated with HMAC-SHA-256 over
 * (iv || ciphertext).
 */
export async function encrypt(
  plaintext: string,
  key: string,
): Promise<EncryptedEnvelopeV2> {
  if (typeof plaintext !== 'string') {
    throw new Error('encrypt: plaintext must be a string');
  }
  const { encKey, macKey } = splitKey(key);
  const iv = await Aes.randomKey(IV_BYTES);
  const ciphertext = await Aes.encrypt(plaintext, encKey, iv, 'aes-256-cbc');
  const tag = await Aes.hmac256(iv + ciphertext, macKey);
  return {
    version: CURRENT_VERSION,
    iv,
    ciphertext,
    tag,
  };
}

/**
 * Decrypt a v2 envelope. The HMAC tag is verified BEFORE decryption; if
 * verification fails a DecryptionError is thrown. There is no fallback to
 * other algorithms or to plaintext.
 */
export async function decrypt(
  envelope: EncryptedEnvelopeV2,
  key: string,
): Promise<string> {
  if (!envelope || typeof envelope !== 'object') {
    throw new DecryptionError('envelope missing');
  }
  if (envelope.version !== CURRENT_VERSION) {
    throw new DecryptionError(
      `unsupported version: ${String(envelope.version)}`,
    );
  }
  const { iv, ciphertext, tag } = envelope;
  if (
    typeof iv !== 'string' ||
    iv.length !== IV_HEX_LEN ||
    typeof ciphertext !== 'string' ||
    typeof tag !== 'string' ||
    tag.length !== 64
  ) {
    throw new DecryptionError('envelope malformed');
  }
  const { encKey, macKey } = splitKey(key);

  const expectedTag = await Aes.hmac256(iv + ciphertext, macKey);
  if (!constantTimeEqualHex(expectedTag, tag)) {
    throw new DecryptionError('authentication tag mismatch');
  }

  try {
    return await Aes.decrypt(ciphertext, encKey, iv, 'aes-256-cbc');
  } catch (err) {
    // Should be unreachable when HMAC matches, but never leak details.
    throw new DecryptionError('ciphertext decryption failed');
  }
}

/**
 * Serialize a v2 envelope to a JSON string safe to persist to disk or
 * AsyncStorage. The reverse is `parseEnvelope`.
 */
export function serializeEnvelope(envelope: EncryptedEnvelopeV2): string {
  return JSON.stringify(envelope);
}

export function parseEnvelope(raw: string): EncryptedEnvelopeV2 {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new DecryptionError('envelope is not valid JSON');
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new DecryptionError('envelope is not an object');
  }
  const { version, iv, ciphertext, tag } = parsed as Record<string, unknown>;
  if (version !== CURRENT_VERSION) {
    throw new DecryptionError(`unsupported version: ${String(version)}`);
  }
  if (
    typeof iv !== 'string' ||
    typeof ciphertext !== 'string' ||
    typeof tag !== 'string'
  ) {
    throw new DecryptionError('envelope malformed');
  }
  return { version: CURRENT_VERSION, iv, ciphertext, tag };
}

/**
 * True when a stored blob is in the v2 format. Used at read sites to decide
 * between v2 decryption and routing through the migration path.
 */
export function isV2Envelope(raw: unknown): boolean {
  if (typeof raw !== 'string') return false;
  try {
    const parsed = JSON.parse(raw);
    return (
      parsed &&
      typeof parsed === 'object' &&
      parsed.version === CURRENT_VERSION &&
      typeof parsed.iv === 'string' &&
      typeof parsed.ciphertext === 'string' &&
      typeof parsed.tag === 'string'
    );
  } catch {
    return false;
  }
}
